import React, { useState } from 'react';
import { Col, Form, FormInstance, Input, Row, Typography } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { TimeZoneSelect } from '@identity/components';
import { WorksMembershipRoleSelect } from 'works/components/works-membership-role-select/works-membership-role-select';
import { MembershipByEmail } from 'works/graphql/works-membership-by-email';
import {
  OnMembershipLoaded,
  WorksMembershipSearchInputByEmail,
} from 'works/components/works-membership-search-input-by-email/works-membership-search-input-by-email';
import { capitalizeFirstLetter } from '@common/services';
import { RolePermissionModal } from '@identity/components/role-permission-modal/role-permission-modal';
import { RolesInfoButton } from '@identity/components/roles-info-button/roles-info-button';
import { PhoneInput } from '@common/components';

const { Text } = Typography;
const hiddenStyle = { style: { display: 'none' } };
const noStyle = {};

type Props = {
  form: FormInstance;
  emailLoaded: boolean;
  onMembershipLoaded: OnMembershipLoaded;
  edit?: boolean;
  isOrganization: boolean;
  onResetEmail?: () => void;
  initialValues: object;
  loading?: boolean;
};

export function MembersAddEditStepForm({
  form,
  emailLoaded,
  edit,
  isOrganization,
  onMembershipLoaded,
  onResetEmail,
  initialValues,
  loading,
}: Props) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  function resetInfoFields() {
    form.resetFields(
      Object.keys(form.getFieldsValue()).filter((value) => value !== 'email')
    );
  }

  function watchEmailField({ email }: Store) {
    if (!emailLoaded) return;
    if (email === undefined) return; // empty values are acceptable here.
    resetInfoFields();
    onResetEmail?.();
  }

  function handleMembershipLoaded(membership: MembershipByEmail) {
    onMembershipLoaded(membership);
  }

  function capitalizeNames() {
    form.setFieldsValue({
      firstName: capitalizeFirstLetter(form.getFieldValue('firstName')),
      lastName: capitalizeFirstLetter(form.getFieldValue('lastName')),
    });
  }

  const fieldsStyle = emailLoaded ? noStyle : hiddenStyle;

  return (
    <>
      <RolePermissionModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      />
      <Form
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={watchEmailField}
        onBlur={capitalizeNames}
        form={form}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              hasFeedback={emailLoaded}
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: 'Please enter a valid email',
                },
              ]}
              name="email"
              label="Email"
              extra={
                emailLoaded &&
                edit && (
                  <Text>
                    We recognized this email and pre-filled available details.
                  </Text>
                )
              }
            >
              <WorksMembershipSearchInputByEmail
                placeholder="Type and press enter"
                onMembershipLoaded={handleMembershipLoaded}
                loadOnMount={!emailLoaded}
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} {...fieldsStyle}>
          <Col span={8}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Please enter the first name',
                },
              ]}
              name="firstName"
              label="First Name"
            >
              <Input size="large" placeholder="First Name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              rules={[
                { required: true, message: 'Please enter the last name' },
              ]}
              name="lastName"
              label="Last Name"
            >
              <Input size="large" placeholder="Last Name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} {...fieldsStyle}>
          <Col span={8}>
            <Form.Item name="employeeId" label="Employee ID">
              <Input size="large" placeholder="Enter Employee ID" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[
                {
                  pattern: /^(\d{3}-){2}\d{4}$/,
                  message: 'Phone numbers must be in xxx-xxx-xxxx format.',
                  required: false,
                },
              ]}
            >
              <PhoneInput size="large" placeholder="Enter phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} {...fieldsStyle}>
          <Col span={8}>
            <Form.Item
              rules={[{ required: true, message: 'Please select a role' }]}
              name="role"
              label={
                <>
                  <span>Role</span>
                  {isOrganization && (
                    <RolesInfoButton
                      onClick={() => setShowConfirmModal(true)}
                    />
                  )}
                </>
              }
            >
              <WorksMembershipRoleSelect placeholder="Select a role" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="title" label="Title">
              <Input size="large" placeholder="Membership Title" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} {...fieldsStyle}>
          <Col span={16}>
            <Form.Item name="timeZone" label="Time Zone">
              <TimeZoneSelect />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
}
